import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, input } from '@angular/core';
import { ActivityEntityName, Entity } from '@jms/model';
import { requireSurfaceEntityMetadata } from '@workbuddy/entities/require.surface.entity.metadata';
import { injectDialogRef } from '@workbuddy/ui/v2/blocks/dialog';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { MetadataService } from '@workbuddy/ui/services/metadata.service';
import { WbSurfaceComponent, defineSurface } from '@workbuddy/ui/v2/surface.engine';
import { OutlineCrewSelection, outlineCrewSelection } from './outline.set.crew.model';
import { OutlineSetCrewFormModel, buildOutlineSetCrewSchema } from './outline.set.crew.schema';
import { outlineSetCrewDialogBridgeStyles, outlineSetCrewDialogStyleId } from './outline.set.crew.dialog.theme';

/**
 * Set crew, as a Surface Engine submit Form.
 *
 * The two crew binds are rendered by the engine rather than by raw `wb-select` controls so the
 * dialog inherits `wb-field`'s control chrome — the `.control` box, the `:focus-within` border, the
 * label track, the dirty and error rails — instead of re-deriving each of those from scratch. It is
 * the same schema shape as the Crew section of the Service Line Detail, so setting crew from the
 * outline and setting it on the line itself present the same two controls.
 *
 * The dialog keeps only what the engine has no opinion about: the title, the one-line sentence that
 * says how many lines Apply will write to, and the prompt-width chrome. Cancel and Apply are the
 * Form host's own submit footer (`saveStrategy: 'submit'`), which is also what makes Apply follow
 * the surface's dirty state without this component tracking edits itself.
 */
@Component({
  selector: 'service-plan-outline-set-crew-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [WbSurfaceComponent],
  templateUrl: './outline.set.crew.dialog.html',
  styleUrls: ['./outline.set.crew.dialog.less'],
})
export class OutlineSetCrewDialogComponent {

  private readonly document = inject(DOCUMENT);
  private readonly metadataService = inject(MetadataService);
  private readonly dialogRef = injectDialogRef<OutlineCrewSelection>();

  protected readonly titleId = `service-plan-set-crew-title-${crypto.randomUUID()}`;

  /** How many lines Apply will write to — drives the copy only. */
  public readonly $lineCount = input(0, { alias: 'lineCount' });
  /** Prefill, supplied only when exactly one line is selected; otherwise the pickers start empty. */
  public readonly $contractorValue = input<Entity | null>(null, { alias: 'contractor' });
  public readonly $resourcesValue = input<Entity[]>([], { alias: 'resources' });

  protected readonly $description = computed(() => {

    const count = this.$lineCount();
    return count === 1 ? 'Applies to the selected Service Line.' : `Applies to ${count} selected Service Lines.`;
  });

  /**
   * One definition per set of seed values. The engine treats `initialModel` as the clean baseline,
   * so a prefilled single-line edit starts undirty and Apply stays disabled until something moves —
   * the rule the old hand-rolled `$canApply` computed was trying to express.
   */
  protected readonly $definition = computed(() => {

    const initialModel: OutlineSetCrewFormModel = { defaultContractor: this.$contractorValue() ?? null, defaultResources: this.$resourcesValue() ?? [] };
    return defineSurface<OutlineSetCrewFormModel>({
      schema: buildOutlineSetCrewSchema(),
      entityMetadata: requireSurfaceEntityMetadata(this.metadataService, ActivityEntityName.ServiceLine),
      mode: 'create',
      initialModel,
      saveStrategy: 'submit',
      onSubmit: event => {
        // Tiny-ref projection stays in the model file: the picker hands back a fat option and the
        // API stores an embedded ref, so only `_id` and `name` may be written.
        const selection = outlineCrewSelection(event.model?.defaultContractor, event.model?.defaultResources);
        this.dialogRef.close(selection);
        return selection;
      },
      onCancel: () => this.dialogRef.dismiss(),
    });
  });

  public constructor() {

    DesignSystem.Mount(this.document, outlineSetCrewDialogStyleId, outlineSetCrewDialogBridgeStyles);
  }
}
